/**
 * Returns true when a migration warning relates to division remapping so it can be
 * visually emphasised in warning banners. Matches the backend `[DIV-MAP]` /
 * `[DIV-PRUNE]` / `[DIV-PRUNE-UNRESOLVED]` / `[DIV-PREFLIGHT]` / `[DIV-CLOSURE]` /
 * `[RESOURCE-CHECK]` prefixes and any warning that mentions "division".
 */
export function isDivisionWarning(warning: string): boolean {
  const lower = warning.toLowerCase();
  return (
    lower.includes('[div-map]') ||
    lower.includes('[div-prune]') ||
    lower.includes('[div-prune-unresolved]') ||
    lower.includes('[div-preflight]') ||
    lower.includes('[div-closure]') ||
    (lower.includes('[resource-check]') && lower.includes('division')) ||
    lower.includes('division')
  );
}

/** Prefix the backend prepends to import-reconciliation warnings on `job3Import.warnings`. */
export const RECONCILIATION_PREFIX = '[RECONCILIATION]';

/**
 * Returns true when an import warning is an end-to-end reconciliation line emitted by the backend's
 * `ImportReconciliationBuilder` — i.e. the "Not fully migrated" rollup or a per-resource-type
 * `requested/imported/dropped` breakdown. Used to route these warnings into the dedicated
 * "Not fully migrated" banner rather than the generic warnings list.
 */
export function isReconciliationWarning(warning: string): boolean {
  return warning.toUpperCase().includes(RECONCILIATION_PREFIX);
}

/** Strips the leading `[RECONCILIATION]` marker (and surrounding whitespace) for display. */
export function stripReconciliationPrefix(warning: string): string {
  const index = warning.toUpperCase().indexOf(RECONCILIATION_PREFIX);
  if (index < 0) {
    return warning.trim();
  }
  return warning.slice(index + RECONCILIATION_PREFIX.length).trim();
}

/**
 * Returns true when a reconciliation warning is the top-level "Not fully migrated" rollup line
 * (as opposed to a per-resource-type breakdown line).
 */
export function isReconciliationRollup(warning: string): boolean {
  return isReconciliationWarning(warning) && /not fully migrated/i.test(warning);
}

/**
 * Prefix the Exporter prepends to snapshot flow-completeness warnings on `job1Export.warnings`.
 * Emitted only for snapshot exports when one or more draft-only flows (no published version) could
 * not be captured by the published-only CX-as-Code flow exporter.
 */
export const SNAPSHOT_INCOMPLETE_PREFIX = '[SNAPSHOT-INCOMPLETE]';

/**
 * Returns true when an export warning is a snapshot flow-completeness line — i.e. one or more
 * draft-only flows were left out of the snapshot because the Genesys Cloud platform only exports
 * flows that have a published version.
 */
export function isSnapshotIncompleteWarning(warning: string): boolean {
  return warning.toUpperCase().includes(SNAPSHOT_INCOMPLETE_PREFIX);
}

/** Strips the leading `[SNAPSHOT-INCOMPLETE]` marker (and surrounding whitespace) for display. */
export function stripSnapshotIncompletePrefix(warning: string): string {
  const index = warning.toUpperCase().indexOf(SNAPSHOT_INCOMPLETE_PREFIX);
  if (index < 0) {
    return warning.trim();
  }
  return warning.slice(index + SNAPSHOT_INCOMPLETE_PREFIX.length).trim();
}

/** Prefix the Importer prepends to target-org readiness pre-flight findings (F-06/F-08/F-09/F-19/F-22). */
export const TARGET_READINESS_PREFIX = '[TARGET-READINESS]';

/** Prefix the Exporter prepends to deprecated-flow-type warnings on `job1Export.warnings` (F-28). */
export const DEPRECATED_FLOW_TYPE_PREFIX = '[FLOW-TYPE-DEPRECATED]';

/**
 * Returns true when a warning is a target-org readiness pre-flight finding — a prerequisite the
 * operator must satisfy in the target org before the migration can fully land (missing TTS voice,
 * duplicate script/queue/data action, division cap, emergency-number collision).
 */
export function isTargetReadinessWarning(warning: string): boolean {
  return warning.toUpperCase().includes(TARGET_READINESS_PREFIX);
}

/** Strips the leading `[TARGET-READINESS]` marker (and surrounding whitespace) for display. */
export function stripTargetReadinessPrefix(warning: string): string {
  const index = warning.toUpperCase().indexOf(TARGET_READINESS_PREFIX);
  if (index < 0) {
    return warning.trim();
  }
  return warning.slice(index + TARGET_READINESS_PREFIX.length).trim();
}

/**
 * Returns true when a readiness finding is a duplicate-name blocker — a target-org resource
 * (data action, script, queue) whose name matches more than one record, so a flow that resolves
 * it by name cannot disambiguate and fails to publish (F-06/F-19). The backend categories all start
 * with `Duplicate` (e.g. `DuplicateDataAction`, `DuplicateScript`, `DuplicateQueue`).
 */
export function isDuplicateReadinessWarning(warning: string): boolean {
  if (!isTargetReadinessWarning(warning)) {
    return false;
  }
  return /^Duplicate\w*/i.test(stripTargetReadinessPrefix(warning));
}

/**
 * Returns true when a warning flags a flow of a platform-deprecated type (e.g. `inboundchat`) that
 * can never publish and must be rebuilt as a supported type before migrating (F-28).
 */
export function isDeprecatedFlowTypeWarning(warning: string): boolean {
  return warning.toUpperCase().includes(DEPRECATED_FLOW_TYPE_PREFIX);
}

/** Strips the leading `[FLOW-TYPE-DEPRECATED]` marker (and surrounding whitespace) for display. */
export function stripDeprecatedFlowTypePrefix(warning: string): string {
  const index = warning.toUpperCase().indexOf(DEPRECATED_FLOW_TYPE_PREFIX);
  if (index < 0) {
    return warning.trim();
  }
  return warning.slice(index + DEPRECATED_FLOW_TYPE_PREFIX.length).trim();
}

/**
 * Returns the target-org readiness findings for a migration — the readiness pre-flight results from
 * the transform/import jobs plus any deprecated-flow-type warning from the export job. Surfaced in the
 * dedicated {@link TargetReadinessPanel} rather than the generic "Manual steps required" list.
 */
export function getTargetReadinessWarnings(job: {
  job1Export?: { warnings?: string[] };
  job2Transform?: { warnings?: string[] };
  job3Import?: { warnings?: string[] };
}): string[] {
  const all = [
    ...(job.job1Export?.warnings ?? []),
    ...(job.job2Transform?.warnings ?? []),
    ...(job.job3Import?.warnings ?? []),
  ];
  return all.filter((w) => isTargetReadinessWarning(w) || isDeprecatedFlowTypeWarning(w));
}

/**
 * High-level buckets used to group and label migration warnings in the review banner so a long,
 * flat list becomes scannable. Ordering in {@link WARNING_CATEGORY_ORDER} drives display order.
 */
export type WarningCategory =
  | 'targetReadiness'
  | 'flowDependency'
  | 'division'
  | 'snapshotIncomplete'
  | 'general';

/** Display order for grouped warning categories (most actionable first). */
export const WARNING_CATEGORY_ORDER: readonly WarningCategory[] = [
  'targetReadiness',
  'flowDependency',
  'division',
  'snapshotIncomplete',
  'general',
];

/** Human-readable label + one-line legend description per warning category. */
export const WARNING_CATEGORY_META: Record<
  WarningCategory,
  { label: string; description: string }
> = {
  targetReadiness: {
    label: 'Target org readiness',
    description:
      'Manual steps or prerequisites needed in the target org — missing TTS/STT voices or engines, duplicate names, or unsupported resource types.',
  },
  flowDependency: {
    label: 'Flow dependencies',
    description:
      'Resources a flow depends on that were auto-extracted, co-exported, or could not be migrated automatically.',
  },
  division: {
    label: 'Division remapping',
    description:
      'A division_id remap was skipped, or an org-scoped resource is being reassigned to the target division and may leave source-division dependents orphaned.',
  },
  snapshotIncomplete: {
    label: 'Snapshot completeness',
    description: 'Draft-only flows without a published version were left out of the snapshot.',
  },
  general: {
    label: 'Other warnings',
    description: '',
  },
};

/**
 * Classifies a warning string into a {@link WarningCategory} using its leading backend prefix.
 * Specific prefixes are matched before the broad "division" substring test so a flow-dependency or
 * readiness warning is never mis-bucketed as a division warning.
 */
export function categorizeWarning(warning: string): WarningCategory {
  const upper = warning.toUpperCase();
  if (upper.includes('[FLOW-DEP')) return 'flowDependency';
  if (upper.includes('[TARGET-READINESS]')) return 'targetReadiness';
  if (upper.includes('[FLOW-TYPE-DEPRECATED]')) return 'targetReadiness';
  if (isSnapshotIncompleteWarning(warning)) return 'snapshotIncomplete';
  if (upper.includes('[DIV-REASSIGN')) return 'division';
  if (isDivisionWarning(warning)) return 'division';
  return 'general';
}

/** A group of warnings sharing a {@link WarningCategory}, with its display label and legend. */
export interface WarningGroup {
  category: WarningCategory;
  label: string;
  description: string;
  items: string[];
}

/**
 * Groups warnings by {@link WarningCategory} in {@link WARNING_CATEGORY_ORDER}. Empty categories are
 * omitted. When every warning falls into a single category the caller may choose to render a flat
 * list instead of grouped sections.
 */
export function groupWarnings(warnings: string[]): WarningGroup[] {
  const buckets = new Map<WarningCategory, string[]>();
  for (const warning of warnings) {
    const category = categorizeWarning(warning);
    const list = buckets.get(category) ?? [];
    list.push(warning);
    buckets.set(category, list);
  }

  return WARNING_CATEGORY_ORDER.filter((category) => buckets.has(category)).map((category) => ({
    category,
    label: WARNING_CATEGORY_META[category].label,
    description: WARNING_CATEGORY_META[category].description,
    items: buckets.get(category) ?? [],
  }));
}


/** Returns true for post-completion warnings that require manual follow-up steps. */
export function isManualRemediationWarning(warning: string): boolean {
  const lower = warning.toLowerCase();
  return (
    lower.includes('[cascade]') ||
    lower.includes('[manual-steps]') ||
    lower.includes('[flow-repair]')
  );
}

/**
 * Returns true when a `[RESOURCE-CHECK]` warning is a benign informational reuse notice — the
 * pre-import check found resources that already exist in the target org and generated import
 * blocks so Terraform imports (reuses) them instead of creating duplicates. This is the system
 * working correctly and needs no manual action, so it must NOT surface under "Manual steps
 * required". Genuinely actionable `[RESOURCE-CHECK]` lines (e.g. `SKIPPED ...` continue-on-error
 * validation failures) are excluded here so they still count as manual steps.
 */
export function isInformationalResourceCheckWarning(warning: string): boolean {
  const lower = warning.toLowerCase();
  if (!lower.includes('[resource-check]')) return false;
  if (lower.includes('skipped')) return false;
  return (
    lower.includes('import blocks generated to prevent duplicates') ||
    lower.includes('will be reused') ||
    lower.includes('imported (reused)')
  );
}

/**
 * Returns true when a warning is a dry-run / preview status note (`[DRY-RUN]` or `[PREVIEW]`). These
 * are surfaced by the dedicated dry-run banner and the migration preview panel, so they must not also
 * appear under "Manual steps required".
 */
export function isPreviewNoticeWarning(warning: string): boolean {
  const lower = warning.toLowerCase();
  return lower.includes('[dry-run]') || lower.includes('[preview]');
}

/**
 * Returns the set of warnings shown under "Manual steps required" for a completed
 * (or partially-completed) non-snapshot migration: every transform/import warning that is
 * NOT a pure division-remapping warning (those have their own dedicated banner). Cascade /
 * manual-remediation warnings and any other non-division warnings are included here so no
 * warning is ever silently dropped or shown twice.
 */
export function getManualStepsWarnings(job: {
  isSnapshot?: boolean;
  status: string | number;
  job2Transform?: { warnings?: string[] };
  job3Import?: { warnings?: string[] };
}): string[] {
  if (job.isSnapshot) return [];
  const statusStr = String(job.status);
  const isComplete =
    statusStr === 'completed' ||
    statusStr === 'PartiallyCompleted' ||
    Number(job.status) === 9 ||
    Number(job.status) === 10;
  if (!isComplete) return [];
  const all = [
    ...(job.job2Transform?.warnings ?? []),
    ...(job.job3Import?.warnings ?? []),
  ];
  // A pure-division warning is shown in the Division banner; target-org readiness findings have their
  // own dedicated panel; benign resource-check reuse notices are informational; everything else lands here.
  return all.filter(
    (w) =>
      !(isDivisionWarning(w) && !isManualRemediationWarning(w)) &&
      !isTargetReadinessWarning(w) &&
      !isDeprecatedFlowTypeWarning(w) &&
      !isInformationalResourceCheckWarning(w) &&
      !isPreviewNoticeWarning(w),
  );
}

/**
 * Returns true when a completed (or partially-completed) non-snapshot migration carries
 * any warning that should be surfaced under "Manual steps required" (cascade exclusions or
 * other non-division warnings).
 */
export function hasCompletionWarnings(job: {
  isSnapshot?: boolean;
  status: string | number;
  job2Transform?: { warnings?: string[] };
  job3Import?: { warnings?: string[] };
}): boolean {
  return getManualStepsWarnings(job).length > 0;
}
